import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FaTimes, FaChevronRight, FaWpforms } from 'react-icons/fa';

import { WIDGET_STATS_SETTINGS } from '../../config';
import { CLICK_ACTIONS } from '../../constants';
import { useOpenOldWidget } from 'hooks';

import { toggle } from '../../features/MiniBookingWidget/MiniBookingWidgetSlice';

import './MobileBookingWidget.css';

import {
    applyImportantStyles,
    getWidgetPosition,
    makeStatsRequestThrottled,
} from '../../lib';
import { openDrawer } from 'features/drawersSlice';

import Channel from '../channel/Channel';
import MobileWidgetHeaderBtn from '../mobileWidgetHeaderBtn/MobileWidgetHeaderBtn';
import { OffersButton } from '../offers';
import ToggleWidgetRoundBtn from '../common/toggleWidgetRoundBtn/ToggleWidgetRoundBtn';
import Stars from '../common/stars/Stars';
import { NoAvailablePrices } from 'features/noAvailablePrices';
import { OldWidgetLoadingIndicator } from 'components/common/oldWidgetLoadingIndicator';
import { openModal } from 'features/modalSlice';

const MobileBookingWidget = ({ showWidget }) => {
    const isOpen = useSelector((state) => state.miniBookingWidget.open);
    const hidden = useSelector((state) => state.miniBookingWidget.hidden);
    const price = useSelector((state) => state.miniBookingWidget.price);

    const data = useSelector((state) => state.miniBookingWidget?.data) || {};
    const channels =
        useSelector((state) => state.miniBookingWidget?.channels) || {};
    const showOffer = data?.enableWidgetOffers;
    const Lang = data?.Lang;
    const {
        Behavior: clickAction,
        ourPrice,
        ourOffers,
        colors,
        poweredBy,
        toggleWidgetRoundBtn,
        enableXButton: enableXBtn,
        findLowestPriceMessage: lowestPriceMessage,
        disablearrow: disableArrowBtn,
        bookingRefText,
        totalScore,
        totalReviews,
        propertyID: propertyId,
        noPricesMessage,
    } = data;

    const show = useSelector((state) => state.miniBookingWidget.show);
    const widgetPosition = useSelector(
        (state) => state.miniBookingWidget.widgetPosition
    );

    const widgetPositionStyles = getWidgetPosition(widgetPosition);

    const miniWidgetStyles = {
        ...widgetPositionStyles,
        display: hidden ? 'none' : 'block',
        'max-width': disableArrowBtn ? '240px' : '250px',
    };

    const mobileWidgetHeaderBtnStyles = {
        // fontSize:
        //     channels.length === 0 && ourOffers?.url === '' ? '16px' : '18px',
        fontSize: '16px',
    };

    const dispatch = useDispatch();

    const toggleWidget = () => {
        dispatch(toggle());
    };

    const { showLoader: showOldWidgetLoader, clickHandler: openOldWidget } =
        useOpenOldWidget();

    const onClickOffer = (event) => {
        event.stopPropagation();
        dispatch(
            openModal({
                modalId: 'OfferModal',
            })
        );
    };

    const onClickReviews = (event) => {
        event.stopPropagation();
        dispatch(
            openModal({
                modalId: 'ReviewsModal',
            })
        );
    };

    const onClick = useCallback(() => {
        if (clickAction === CLICK_ACTIONS.OPEN_BIG_PLUGIN) {
            dispatch(openDrawer({ drawerId: 'MobileMultipropertyDrawer' }));
            if (!document.body.classList.contains('ab-no-overflow')) {
                document.body.classList.add('ab-no-overflow');
            }
        } else {
            openOldWidget(clickAction);
        }
        makeStatsRequestThrottled(
            WIDGET_STATS_SETTINGS.actions.click,
            propertyId
        );
    }, [clickAction]);

    const chatBtnHandleClick = useCallback(() => {
        openOldWidget(CLICK_ACTIONS.OPEN_CHAT);
    }, []);

    const renderLowestPriceMsg = () => {
        if (clickAction === CLICK_ACTIONS.OPEN_PLUGIN) {
            return (
                <div className="ab-lowest-price-msg ab-lowest-price-msg--mobile">
                    {lowestPriceMessage}
                </div>
            );
        }

        return (
            <a
                className="ab-lowest-price-msg ab-lowest-price-msg--mobile"
                href={ourPrice?.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                    makeStatsRequestThrottled(
                        WIDGET_STATS_SETTINGS.actions.click,
                        propertyId
                    )
                }
            >
                <div>{lowestPriceMessage}</div>
            </a>
        );
    };

    const inlineStyles = {};
    if (data.pluginZIndex) {
        inlineStyles.zIndex = data.pluginZIndex;
    }

    if (!show) {
        return null;
    }

    const hasPrices = ourPrice?.price || ourPrice?.loyaltyprice;
    const showNoPricesMessage = !hasPrices && noPricesMessage;
    const showHorizontalDivider =
        (channels.length > 0 && (totalScore || totalReviews)) ||
        (channels.length === 0 &&
            lowestPriceMessage &&
            (totalScore || totalReviews));

    return (
        <div
            className={`ab-mini-widget ${
                !isOpen ? 'ab-mini-widget-closed' : ''
            }`}
            style={inlineStyles}
            ref={(el) => {
                applyImportantStyles(el, miniWidgetStyles);
            }}
        >
            {isOpen && (
                <>
                    <div className="ab-mini-widget-top-section">
                        <OffersButton offers={ourOffers} isOpen />
                        <button
                            className="ab-mini-widget-close-btn"
                            onClick={toggleWidget}
                        >
                            <FaTimes />
                        </button>
                    </div>

                    <div
                        className="ab-mini-widget-content"
                        onClick={
                            clickAction === CLICK_ACTIONS.OPEN_PLUGIN ||
                            clickAction === CLICK_ACTIONS.OPEN_BIG_PLUGIN
                                ? onClick
                                : () => {}
                        }
                        style={{
                            cursor:
                                clickAction === CLICK_ACTIONS.OPEN_PLUGIN ||
                                clickAction === CLICK_ACTIONS.OPEN_BIG_PLUGIN
                                    ? 'pointer'
                                    : 'default',
                        }}
                    >
                        <div
                            className="ab-mini-widget-header"
                            style={{
                                backgroundColor: colors?.primaryBackgroundColor,
                            }}
                        >
                            <div
                                className={
                                    disableArrowBtn
                                        ? 'ab-mini-booking-widget-rates--full'
                                        : 'ab-mini-booking-widget-rates'
                                }
                                style={{
                                    padding: showNoPricesMessage
                                        ? '15px 0px'
                                        : '0px',
                                }}
                            >
                                {bookingRefText && (
                                    <span
                                        className="ab-mini-widget-booking-ref"
                                        style={{
                                            color: colors.primaryTextColor,
                                        }}
                                    >
                                        {bookingRefText}
                                    </span>
                                )}

                                {/* Loyalty Price */}
                                <MobileWidgetHeaderBtn
                                    price={ourPrice?.loyaltyprice}
                                    priceText={ourPrice?.loyaltytext}
                                    currencySymbol={ourPrice?.currencySymbol}
                                    url={
                                        clickAction ===
                                            CLICK_ACTIONS.OPEN_PLUGIN ||
                                        clickAction ===
                                            CLICK_ACTIONS.OPEN_BIG_PLUGIN
                                            ? ''
                                            : ourPrice?.loyaltyurl
                                    }
                                    colors={colors}
                                    handleClick={() =>
                                        makeStatsRequestThrottled(
                                            WIDGET_STATS_SETTINGS.actions.click,
                                            propertyId
                                        )
                                    }
                                    disableArrowBtn={disableArrowBtn}
                                    classes="ab-loyalty-price"
                                    styles={{
                                        buttonStyles:
                                            mobileWidgetHeaderBtnStyles,
                                    }}
                                />

                                {/* Best Rate Price */}
                                <MobileWidgetHeaderBtn
                                    price={price}
                                    priceText={ourPrice?.text}
                                    currencySymbol={ourPrice?.currencySymbol}
                                    url={
                                        clickAction ===
                                            CLICK_ACTIONS.OPEN_PLUGIN ||
                                        clickAction ===
                                            CLICK_ACTIONS.OPEN_BIG_PLUGIN
                                            ? ''
                                            : ourPrice?.url
                                    }
                                    colors={colors}
                                    handleClick={() =>
                                        makeStatsRequestThrottled(
                                            WIDGET_STATS_SETTINGS.actions.click,
                                            propertyId
                                        )
                                    }
                                    disableArrowBtn={disableArrowBtn}
                                    showAlways={false}
                                    classes="ab-best-rate-price"
                                    styles={{
                                        containerStyles: {
                                            borderBottom:
                                                colors.primaryBackgroundColor.toUpperCase() ===
                                                    '#FFF' ||
                                                colors.primaryBackgroundColor.toUpperCase() ===
                                                    '#FFFFFF'
                                                    ? `1px solid #e4e4e4`
                                                    : 'none',
                                        },
                                        buttonStyles:
                                            mobileWidgetHeaderBtnStyles,
                                    }}
                                />
                            </div>
                            {/* {!disableArrowBtn && hasPrices && (
                                <button
                                    style={{
                                        backgroundColor:
                                            colors?.secondaryBackgroundColor,
                                        color: colors?.primaryTextColor,
                                    }}
                                    className="ab-our-price-arrow"
                                    ref={(el) => {
                                        applyImportantStyles(el, {
                                            margin: '0',
                                            'background-color':
                                                colors?.secondaryBackgroundColor,
                                            color: colors?.primaryTextColor,
                                        });
                                    }}
                                    onClick={() => {
                                        const bestRateButton =
                                            document.querySelector(
                                                '.ab-best-rate-price button'
                                            );
                                        if (bestRateButton) {
                                            bestRateButton.click();
                                        }
                                    }}
                                >
                                    <FaChevronRight />
                                </button>
                            )} */}
                        </div>
                        <div className="ab-mini-widget-body">
                            {hasPrices && channels?.length > 0 && (
                                <div className="ab-channels">
                                    {channels.map((channel, index) => {
                                        return (
                                            <Channel
                                                disablePointerEvents={
                                                    clickAction ===
                                                        CLICK_ACTIONS.OPEN_PLUGIN ||
                                                    clickAction ===
                                                        CLICK_ACTIONS.OPEN_BIG_PLUGIN
                                                }
                                                url={
                                                    clickAction ===
                                                        CLICK_ACTIONS.OPEN_PLUGIN ||
                                                    clickAction ===
                                                        CLICK_ACTIONS.OPEN_BIG_PLUGIN
                                                        ? ''
                                                        : channel?.url
                                                }
                                                handleClick={() => {
                                                    makeStatsRequestThrottled(
                                                        WIDGET_STATS_SETTINGS
                                                            .actions.click,
                                                        propertyId
                                                    );
                                                }}
                                                logo={channel?.logo}
                                                name={channel?.name}
                                                currencySymbol={
                                                    channel?.currencySymbol
                                                }
                                                price={channel?.price}
                                                key={channel?.channelID}
                                                channelId={channel?.channelID}
                                            />
                                        );
                                    })}
                                </div>
                            )}

                            {channels.length === 0 &&
                            lowestPriceMessage &&
                            price
                                ? renderLowestPriceMsg()
                                : ''}

                            {showNoPricesMessage && (
                                <NoAvailablePrices
                                    message={noPricesMessage}
                                    variant="mobile"
                                />
                            )}

                            {showHorizontalDivider && (
                                <hr className="ab-horizontal-divider-solid" />
                            )}
                            {showOffer && !data.isWebHotelier && (
                                <>
                                    <div
                                        className="ab-mini-widget-stars-and-reviews"
                                        onClick={onClickOffer}
                                    >
                                        <div className="ab-widget-row">
                                            <div className="ab-desktop-offer-button">
                                                {<FaWpforms size={15} />}
                                                <span
                                                    className="ab-get-offer-button-text"
                                                    style={{
                                                        marginLeft: '5px',
                                                    }}
                                                >
                                                    {Lang?.getASecretOffer}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <hr className="ab-horizontal-divider-solid"></hr>
                                </>
                            )}
                            {(totalScore || totalReviews) && (
                                <div
                                    className="ab-mini-widget-stars-and-reviews"
                                    onClick={onClickReviews}
                                >
                                    {totalScore && (
                                        <>
                                            <Stars rating={1} size={18} />{' '}
                                            <span className="ab-mini-widget-score">
                                                {totalScore}
                                            </span>
                                        </>
                                    )}

                                    {totalReviews && (
                                        <span className="ab-mini-widget-reviews">
                                            ({totalReviews})
                                        </span>
                                    )}

                                    <span>
                                        <FaChevronRight />
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="ab-mini-widget-footer">
                            <hr />
                            <div>
                                <span
                                    ref={(el) => {
                                        applyImportantStyles(el, {
                                            'font-size': '12px',
                                            color: 'black',
                                        });
                                    }}
                                >
                                    Powered by &nbsp;
                                </span>
                                <a
                                    href={poweredBy?.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={poweredBy?.logo}
                                        alt=""
                                        style={{ width: '70px' }}
                                        ref={(el) => {
                                            applyImportantStyles(el, {
                                                display: 'unset',
                                                float: 'unset',
                                            });
                                        }}
                                    />
                                </a>
                            </div>
                        </div>
                        {showOldWidgetLoader && <OldWidgetLoadingIndicator />}
                    </div>
                </>
            )}

            <ToggleWidgetRoundBtn
                chatBtnOnClick={chatBtnHandleClick}
                toggleWidgetRoundBtn={toggleWidgetRoundBtn}
                toggleWidget={toggleWidget}
                widgetPosition={widgetPosition}
                showWidget={showWidget}
                enableXBtn={enableXBtn}
                ourPrice={ourPrice}
                showOnlyWhenClosed={toggleWidgetRoundBtn?.showOnlyWhenClosed}
            />
        </div>
    );
};

MobileBookingWidget.propTypes = {
    showWidget: PropTypes.func,
};

export default MobileBookingWidget;
